/**
 * Give applications credit for the value of matching funds we expect them to receive.
 *
 * 1. First run the aggregator once to calculate an X value that the org would be rewarded ignoring matching, called X_O.
 * 2. Then calculate what portion of X_O are matching funds M vs unconditional funds U (so M + U = X_O).
 * 3. Then temporarily assume that a fraction F of matching funds will actually end up being matched; F = 75% by default.
 * 4. Then use the total value algorithm (used for speculation grant settlement) to calculate these values:
 *    - v1 = the total value of the first $U they got
 *    - v2 = (1+1/r)*(the total value of the next F*M dollars they got), where r is their requested matching ratio.
 *      E.g. if they requested 0.25 Jaan dollars for every new donor dollar, then r=0.25 and the multiplier (1+1/r) is 5.
 *    - v3 = (1-F)*(the value of the average hold function)
 * 5. Then let A = (v1+v2+v3)/X_0, and then replace each recommender's value function v_i by A*v_i and rerun the whole aggregator
 *    (once; not finding a fixed point).
 */
import { getHoldValue } from "@engine/round/accessors";
import { trancheListToAlgoTranches } from "@lib/algoInterface/trancheListToAlgoInterface";
import { mvfProfileFromTrancheProfile, TrancheProfile } from "@lib/tranche";
import { assert } from "@lib/utils/utils";
import Matrix from "./matrix";
import { LegacyMVF } from "./mvfs";
import { singleLayerSProcess } from "./singleLayerSProcess";
import Vector from "./vector";

export type Point = [number, number];

/**
 * Interpolate the value at x for the line segment (p0, p1).
 */
export function interpolate(
  [x0, y0]: Point,
  [x1, y1]: Point,
  x: number,
): number {
  const m = (y1 - y0) / (x1 - x0);
  const b = y0 - m * x0;
  return m * x + b;
}

/**
 * Integrate a piecewise linear MVF from 0 to lastDollarThreshold (area under the MVF).
 */
function integrateMVF(mvf: LegacyMVF, lastDollarThreshold: number): number {
  let result = 0;
  for (let i = 0; i < mvf.length - 1; i++) {
    let p0: Point = [mvf[i].dollar, mvf[i].value];
    let p1: Point = [mvf[i + 1].dollar, mvf[i + 1].value];

    if (p0[0] >= lastDollarThreshold) {
      break;
    }

    if (p1[0] > lastDollarThreshold) {
      p1 = [lastDollarThreshold, interpolate(p0, p1, lastDollarThreshold)];
    }

    const [x0, y0] = p0;
    const [x1, y1] = p1;
    result += ((x1 - x0) * (y0 + y1)) / 2;
  }
  return result;
}

/**
 * Calculate the multipliers for the matching funds for each evaluator.
 *
 * @param budgets - The budgets for each evaluator.
 * @param expectedMatchingFractions - The expected matching fractions for each evaluator and proposal.
 * @param evaluableMaxAllocations - The maximum allocations for each evaluable.
 * @param trancheProfiles - The tranche profiles for each evaluator.
 * @param apps - The applications for each evaluator.
 * @param holdProposalId - The ID of the hold proposal.
 * @returns The multipliers for the matching funds for each evaluator. This has shape (numProposals, numEvaluators).
 */
export function matchingMultipliersForEvaluators(
  budgets: Vector<"evaluator">,
  expectedMatchingFractions: {
    [evaluatorShortname: string]: {
      [proposalId: string]: number;
    };
  },
  evaluableMaxAllocations: { [evaluableId: string]: number | null },
  trancheProfiles: { [evaluatorShortname: string]: TrancheProfile },
  apps: {
    id: string;
    matchLimit: number | null;
    matchRate: number;
    matchStartThreshold: number;
  }[],
  holdProposalId: string,
): Matrix<"proposal+hold", "evaluator"> {
  const evaluatorIds = Object.keys(trancheProfiles);
  const mvfProfiles = Object.fromEntries(
    evaluatorIds.map((evaluatorId) => [
      evaluatorId,
      mvfProfileFromTrancheProfile(trancheProfiles[evaluatorId]),
    ]),
  );
  const evaluableIndicesMap = Object.fromEntries(
    apps.map((app, i) => [app.id, i]),
  );
  const evaluableIdWhitelist = new Set(apps.map((app) => app.id));

  if (evaluableIdWhitelist.size === 0) {
    return Matrix.ones(apps.length, evaluatorIds.length);
  }

  const preferenceLists = Object.fromEntries(
    evaluatorIds.map((evaluatorId) => [
      evaluatorId,
      trancheListToAlgoTranches(
        trancheProfiles[evaluatorId],
        evaluableIndicesMap,
        evaluableMaxAllocations,
        evaluableIdWhitelist,
      ),
    ]),
  );
  const trancheTables = new Array(evaluatorIds.length)
    .fill(null)
    .map((_, i) => preferenceLists[evaluatorIds[i]]);
  const { accounts } = singleLayerSProcess(budgets, trancheTables);

  const rows: Vector<"evaluator">[] = apps.map((app, i) => {
    if (i === 0) {
      // The first proposal is the hold proposal by convention.
      return new Vector<"evaluator">(evaluatorIds.map(() => 1));
    }
    const totalFunds = accounts.getCol(i).sum();

    if (app.matchRate === 0) {
      return new Vector<"evaluator">(evaluatorIds.map(() => 1));
    }

    let matchingFunds: number;
    let unmatchedFunds: number;
    if (app.matchLimit !== null && totalFunds > app.matchLimit) {
      matchingFunds = app.matchLimit;
      unmatchedFunds = totalFunds - matchingFunds;
    } else if (
      app.matchStartThreshold !== null &&
      totalFunds > app.matchStartThreshold
    ) {
      unmatchedFunds = app.matchStartThreshold;
      matchingFunds = totalFunds - unmatchedFunds;
    } else {
      matchingFunds = 0;
      unmatchedFunds = totalFunds;
    }

    return new Vector<"evaluator">(
      evaluatorIds.map((evaluatorId, j) => {
        if (!(app.id in mvfProfiles[evaluatorId])) {
          // The evaluator hasn't made an MVF for this proposal.
          return 1;
        }
        const mvf = mvfProfiles[evaluatorId][app.id];
        const expectedMatchingFraction =
          expectedMatchingFractions[evaluatorId][app.id];

        const unmatchedValue = integrateMVF(mvf, unmatchedFunds);
        const matchingValue =
          app.matchRate !== 0
            ? (1 + 1 / app.matchRate) *
              (integrateMVF(
                mvf,
                unmatchedFunds + matchingFunds * expectedMatchingFraction,
              ) -
                integrateMVF(mvf, unmatchedFunds))
            : 0;

        const recommenderHoldValue = getHoldValue(
          trancheProfiles[evaluatorIds[j]],
          holdProposalId,
        );
        const holdValue =
          (1 - expectedMatchingFraction) * recommenderHoldValue * matchingFunds;
        const originalValue = integrateMVF(mvf, totalFunds);
        if (originalValue === 0) {
          return 1;
        }

        const multiplier =
          (unmatchedValue + matchingValue + holdValue) / originalValue;
        assert(
          !isNaN(multiplier) && multiplier >= 0,
          `multiplier is NaN or negative (${multiplier})`,
        );
        return multiplier;
      }),
    );
  });

  return Matrix.ofRows(rows);
}

// Given a target dollar amount, how many dollars should S-Process internally allocate?
// (accounting for outside matching dollars)
//
// outsideDollarSubstitutionRate = how many internal dollars is an external dollar worth?
//
// Given the formula:
//
// target = internal + (internal - baseline) / matchRate * successRate * outsideDollarSubstitutionRate
//
// We're solving for internal given all the other values.
export function calculateShrunkInternalDollar(
  targetDollars: number,
  baselineStandardGrantAmount: number,
  matchLimit: number,
  matchRate: number,
  outsideDollarSubstitutionRate: number,
  estimatedSuccessRate: number,
): number {
  if (
    matchRate === 0 ||
    matchLimit === 0 ||
    estimatedSuccessRate === 0 ||
    outsideDollarSubstitutionRate === 0
  ) {
    // Matching dollars are worth 0 for various reasons
    return targetDollars;
  }

  targetDollars = Math.min(targetDollars, matchLimit);
  if (targetDollars < baselineStandardGrantAmount) {
    // We didn't even activate matching so we don't have to account for external dollars
    return targetDollars;
  }

  const s = matchRate / (estimatedSuccessRate * outsideDollarSubstitutionRate);
  return (targetDollars * s + baselineStandardGrantAmount) / (1 + s);
}
